/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React, { type ReactNode } from 'react';

import { ListeTable, ListeTableContainer } from '@mairistem/liste';

import type { Pacs } from '@/types/Pacs';
import { getActeListeColumns } from '@/constants';
import ActeListeTableRow from './ActeListeTableRow';

interface ActeListeTableProps {
  items: Array<Partial<Pacs>>;
  count: number;
  mode?: string;
  perPage: number;
  loading: boolean;
  dashboard: boolean;
  typeActe?: string;
  assistant?: boolean;
  onLoad: any;
  onSort: any;
  onSelect?: (item: Partial<Pacs>) => void;
}

const ActeListeTable = (props: ActeListeTableProps) => {
  const {
    items,
    mode,
    count,
    perPage,
    loading,
    dashboard,
    typeActe,
    assistant,
    onLoad,
    onSort,
    onSelect: onSelectProp,
  } = props;

  const [selectedItemId, setSelectedItemId] = React.useState<number | null>(
    null,
  );

  const renderRow = (item, invalidate) => (
    <ActeListeTableRow
      item={item}
      mode={mode}
      invalidate={invalidate}
      dashboard={dashboard}
      typeActe={typeActe}
      selected={selectedItemId === item?.identifiant}
    />
  );

  const handleSelect = (selectedItem) => {
    if (onSelectProp) {
      setSelectedItemId(selectedItem?.identifiant);
      onSelectProp(selectedItem);
    }
  };

  const {
    dateLabel,
    dateWidth = 2,
    nomLabel,
    parentLabels,
  } = getActeListeColumns(typeActe);

  const renderDashboardHeader = React.useMemo(
    () => (
      <>
        <ListeTable.Column
          name="nomClassement"
          style={{ maxWidth: '10em' }}
          width={7}
        >
          Nom
        </ListeTable.Column>
        <ListeTable.Column name="typeActe" width={2}>
          Type d&apos;acte
        </ListeTable.Column>
      </>
    ),
    [],
  );

  // La reconnaissance affiche à la fois le nom (l'enfant) et les 2 parents :
  // moins de place pour chaque colonne que quand une seule des deux s'affiche.
  const showsBothNomAndParents = !!nomLabel && !!parentLabels;

  const renderStatsHeader = React.useMemo(
    () => (
      <>
        <ListeTable.Column name="numero" sortable width={2}>
          N° acte
        </ListeTable.Column>
        <ListeTable.Column
          name="registre"
          width={showsBothNomAndParents ? 3 : 4}
        >
          Registre
        </ListeTable.Column>
        <ListeTable.Column name="dateActe" sortable width={dateWidth}>
          {dateLabel}
        </ListeTable.Column>
        {nomLabel && (
          <ListeTable.Column
            name="nomClassement"
            width={showsBothNomAndParents ? 3 : 6}
          >
            {nomLabel}
          </ListeTable.Column>
        )}
        {parentLabels && (
          <>
            <ListeTable.Column name="partenaire1" width={3}>
              {parentLabels[0]}
            </ListeTable.Column>
            <ListeTable.Column name="partenaire2" width={3}>
              {parentLabels[1]}
            </ListeTable.Column>
          </>
        )}
      </>
    ),
    [dateLabel, dateWidth, nomLabel, parentLabels, showsBothNomAndParents],
  );

  const listItems = React.useMemo(() => {
    const list = items || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const normalized = (list as any[]).map((item) => {
      if (item?.identifiant == null && item?.id != null) {
        return { ...item, identifiant: item.id };
      }
      return item;
    });
    if (assistant) {
      // @ts-ignore
      return [
        { isAssistantEmptyRow: true, identifiant: 'empty-row' },
        ...normalized,
      ];
    }
    return normalized;
  }, [items, assistant]);

  return (
    <ListeTableContainer
      // @ts-ignore
      items={listItems}
    >
      <ListeTable
        items={listItems}
        loading={loading}
        perPage={perPage}
        count={count}
        onLoad={onLoad}
        onSort={onSort}
        onSelect={handleSelect}
      >
        <ListeTable.Header>
          {/* eslint-disable-next-line no-nested-ternary */}
          {dashboard ? renderDashboardHeader : renderStatsHeader}
        </ListeTable.Header>
        {renderRow as unknown as ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default ActeListeTable;
